import { DEFAULT_LANGUAGE, getLocale, translate } from '../i18n';

let activeLanguage = DEFAULT_LANGUAGE;

export function setTimeFormatLanguage(language) {
  activeLanguage = language || DEFAULT_LANGUAGE;
}

function t(key, values) {
  return translate(activeLanguage, key, values);
}

function getActiveLocale() {
  return getLocale(activeLanguage);
}

export function formatTime(date) {
  if (!date) {
    return t('never');
  }

  return new Intl.DateTimeFormat(getActiveLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function formatTimestamp(unixTimestamp) {
  if (!unixTimestamp) {
    return t('notAvailable');
  }

  return new Intl.DateTimeFormat(getActiveLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(unixTimestamp * 1000));
}

export function formatDelay(delaySeconds) {
  if (!Number.isFinite(delaySeconds)) {
    return t('notAvailable');
  }

  if (Math.abs(delaySeconds) < 60) {
    return t('onTime');
  }

  const absoluteMinutes = Math.round(Math.abs(delaySeconds) / 60);
  return delaySeconds > 0
    ? t('lateMinutes', { minutes: absoluteMinutes })
    : t('earlyMinutes', { minutes: absoluteMinutes });
}

export function formatPredictedTime(predictedTime, scheduledTime) {
  if (predictedTime) {
    return formatTimestamp(predictedTime);
  }

  if (scheduledTime) {
    return formatTimestamp(scheduledTime);
  }

  return t('notAvailable');
}

export function formatDistanceMeters(distanceMeters) {
  if (!Number.isFinite(Number(distanceMeters))) {
    return t('unavailableDistance');
  }

  const normalizedDistance = Number(distanceMeters);

  if (normalizedDistance >= 1000) {
    return `${(normalizedDistance / 1000).toFixed(normalizedDistance >= 10000 ? 0 : 1)} km`;
  }

  return `${Math.round(normalizedDistance)} m`;
}

export function formatDurationSeconds(durationSeconds) {
  if (!Number.isFinite(Number(durationSeconds)) || Number(durationSeconds) <= 0) {
    return '';
  }

  const minutes = Math.max(1, Math.round(Number(durationSeconds) / 60));
  return minutes <= 1 ? t('aboutOneMinute') : t('aboutMinutes', { minutes });
}

export function getPassageArrivalTime(passage) {
  return Number(passage?.predictedTime || passage?.scheduledTime || 0);
}

export function formatArrivalCountdown(arrivalTime) {
  if (!arrivalTime) {
    return t('arrivalUnavailable');
  }

  const secondsUntilArrival = Number(arrivalTime) - Math.floor(Date.now() / 1000);

  if (secondsUntilArrival <= 30) {
    return t('arrivingNow');
  }

  const minutesUntilArrival = Math.max(1, Math.ceil(secondsUntilArrival / 60));
  return minutesUntilArrival === 1
    ? t('arrivingInOneMinute')
    : t('arrivingInMinutes', { minutes: minutesUntilArrival });
}

export function formatDurationMinutes(durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return t('notAvailable');
  }

  const minutes = Math.max(1, Math.round(durationSeconds / 60));

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes} min` : `${hours}h`;
}
